import { IReactionDisposer, makeAutoObservable, reaction } from 'mobx';

import { debounce, isArray, logger, queryAPI } from '@cutwise/utils';

import { AppError } from '@/dmdrive/core/entities/AppError';
import { RotateModel } from '@/dmdrive/topic/components/Topic/TopicStore.constants';
import { groupRemarks } from '@/dmdrive/topic/components/Topic/TopicStore.utils';
import { StateChangeData } from '@/dmdrive/topic/components/Topic/TopicWSStore.types';
import { ToolbarQuery } from '@/dmdrive/topic/constants/query';
import { ConferenceState } from '@/dmdrive/topic/constants/topic';
import { VIEWER_CONTAINER_ID } from '@/dmdrive/topic/constants/viewer';
import { TopicModel, TopicResourceType } from '@/dmdrive/topic/entities/Topic';
import { ViewerSettings, ViewerSettingsOptions } from '@/dmdrive/topic/entities/ViewerSettings';
import { NotifyService } from '@/dmdrive/topic/services/NotifyService';
import { ViewerService } from '@/dmdrive/topic/services/ViewerService';

import { FlatRemark } from '@carbon/dm-industrial/lib/dm-industrial';
import { MeshRegistrationState, RemarkType } from '@carbon/dm-industrial/lib/dm-industrial';

import { TopicStore } from './TopicStore';

export class TopicViewerStore {
  isViewerReady = false;

  viewer: Nullable<ViewerService> = null;
  viewerError: Nullable<AppError> = null;
  viewerSettings = new ViewerSettings();

  histogram: number[] = [];
  recognitionState: Nullable<MeshRegistrationState[]> = null;
  modelToRotate = RotateModel.None;

  isHistogramCalcInProgress = true;
  isRecognitionStarted = false;
  // shouldShowMaxDistancePoints = true;
  useNonSymmetricMetric = false;
  shouldShowRemarks = true;
  remarksType!: RemarkType;
  // remarksType: RemarkType = 'DifferenceMeasurement';

  private reactions: IReactionDisposer[] = [];

  constructor(private topicStore: TopicStore) {
    makeAutoObservable(this, {}, { autoBind: true });

    this.subscribeToReactions();
  }

  get settings() {
    return {
      ...this.viewerSettings.toJS,
      // shouldShowMaxDistancePoints: this.shouldShowMaxDistancePoints,
      useNonSymmetricMetric: this.useNonSymmetricMetric,
      shouldShowRemarks: this.shouldShowRemarks,
      recognitionState: this.recognitionState,

      // do not pass Annotation, because we create and pass text remarks manually,
      // so Viewer should not interact with these remarks
      remarksType: this.remarksType === 'Annotation' ? 'none' : this.remarksType,
    };
  }

  startViewer() {
    if (!this.topicStore.topic) {
      return;
    }

    try {
      switch (this.topicStore.topic.viewerType) {
        case TopicResourceType.ImageViewer:
        case TopicResourceType.LODViewer:
        case TopicResourceType.DMIndustrial:
          this.viewer = new ViewerService(this.topicStore.topic.viewerType);

          break;
        case TopicResourceType.CarbonViewer:
          this.viewer = new ViewerService(
            this.topicStore.topic.isDMIndustrial ? TopicResourceType.DMIndustrial : TopicResourceType.CarbonViewer,
          );

          break;
        default:
          break;
      }

      this.viewer?.create({
        onStateUpdate: (state: string, ...data: unknown[]) => {
          if (state === 'state.ready') {
            this.isViewerReady = true;

            this.savePreview();
          }

          if (state === 'state.error') {
            this.viewerError = new AppError(data[0] as string);
          }
        },
        onMessage: (action: string, data: Record<string, never>) => {
          switch (action) {
            case 'pointer.moved':
              this.topicStore.topicWSStore?.publishPointers?.(data);

              break;
            case 'remark.removed':
              this.topicStore.deleteMarkByMetaId(data['id']);

              break;

            case 'state.updated':
              Object.keys(data).forEach((key: string) => {
                switch (key) {
                  case 'remarks':
                    this.onReceiveMarksFromViewer(data.remarks);

                    break;
                  case 'remarksType':
                  case 'showModel':
                  case 'visualMode':
                  case 'visualModeBoth':
                  case 'palettePreset':
                  case 'histogramScaleView':
                  case 'isPaletteZeroPoint':
                  case 'switchModelsOnHistogram':
                  case 'switchModelsRecognition':
                  case 'paletteLowerBound':
                  case 'paletteUpperBound':
                  case 'histogramLowerLimit':
                  case 'histogramUpperLimit':
                  case 'histogramLowerValue':
                  case 'scopeUpperValue':
                  case 'scopeLowerValue':
                  case 'lowerValuePD':
                  case 'upperValuePD':
                  case 'histogramPlan':
                  case 'histogramResult':
                  case 'histogramActual':
                  case 'enableMaxDistPoints':
                  case 'recoActual':
                  case 'histogramUpperLimitOrig':
                  case 'histogramLowerLimitOrig':
                  case 'maxPoints':
                  case 'showMaxPoints':
                  case 'maxPointDistances':
                    // if (key === 'maxPointDistances' /* || key === 'scopeLowerValue' */) {
                    //   console.log(key, data[key]);
                    // }

                    this.viewerSettings.apply({ [key]: data[key] });

                    break;
                  case 'fitness':

                    this.viewerSettings.apply({ [key]: data[key] });
                    this.topicStore.topic?.setMetaValue('fitness', this.viewerSettings.fitness);

                    break;
                  case 'histogram':
                  case 'isHistogramCalcInProgress':
                  case 'isRecognitionStarted':
                    if (typeof this[key] !== 'undefined' && typeof data[key] !== 'undefined') {
                      this[key] = data[key];
                    }

                    break;
                  case 'camera':
                    this.viewerSettings.apply({ cameraSequence: data.sequence });

                    break;
                  case 'updatePreview':
                    this.savePreviewForce();

                    break;
                  default:
                    break;
                }
              });

              this.publishViewerChanges(data);

              break;
            case 'recognition':
              if (isArray(this.recognitionState)) {
                this.recognitionState!.push(data as unknown as MeshRegistrationState);
              } else {
                this.recognitionState = [data as unknown as MeshRegistrationState];
              }

              this.topicStore.topic?.setMetaValue('fitness', (data as unknown as MeshRegistrationState).fitness);

              break;
            case 'models.position':
              this.viewerSettings.apply({ localTransform: data });

              break;
            default:
              this.publishViewerChanges(data);

              break;
          }
        },
        ...this.cvViewSettings,
      });

      this.topicStore.updateQuery();
    } catch (e) {
      NotifyService.error(e);
    }
  }

  setSettings(settings: Partial<ViewerSettingsOptions>) {
    // console.log("setSettings", settings);
    this.viewerSettings.apply(settings);
  }

  applyWebSocketState(nextState?: StateChangeData) {
    if (!nextState) {
      return;
    }

    const { camera, remarks, ...rest } = nextState;

    if (typeof camera === 'string') {
      this.viewer?.setCameraState(camera);
    }

    if (remarks && isArray(remarks)) {
      this.viewer?.putRemarks?.(remarks);
    }

    this.setSettings(rest);
  }

  setModelToRotate(type: RotateModel) {
    this.modelToRotate = type;
  }

  startRecognition() {
    this.recognitionState = [];
    this.setModelToRotate(RotateModel.None);

    this.viewer?.removeMaxDistance?.();
    this.viewer?.startRecognition?.();
  }

  stopRecognition() {
    this.isRecognitionStarted = false;
    this.viewer?.stopRecognition?.();
  }

  //toggleShowMaxDistancePoints() {
    // this.shouldShowMaxDistancePoints = !this.shouldShowMaxDistancePoints;
  //}

  toggleShowRemarks() {
    this.shouldShowRemarks = !this.shouldShowRemarks;
  }

  toggleNonSymmetricMetric() {
    this.useNonSymmetricMetric = !this.useNonSymmetricMetric;
  }

  setRemarksType(remarksType: RemarkType) {
    this.remarksType = remarksType;
  }

  dispose() {
    this.isViewerReady = false;
    this.viewerError = null;

    this.modelToRotate = RotateModel.None;

    this.histogram = [];
    this.isHistogramCalcInProgress = true;
    this.isRecognitionStarted = false;
    this.recognitionState = null;
    // this.shouldShowMaxDistancePoints = false;
    this.useNonSymmetricMetric = false;
    this.shouldShowRemarks = true;

    this.viewerSettings.reset();

    this.reactions.forEach((dispose) => dispose());
  }

  private get cvViewSettings() {
    return {
      containerId: `#${VIEWER_CONTAINER_ID}`,
      model: this.topicStore.topic?.model ?? '',
      targetModel: this.topicStore.topic?.targetModel ?? '',
      toolbar: queryAPI.getKey(ToolbarQuery.Debug) === '1',
      ...this.viewerSettings.toJS,
    };
  }

  private subscribeToReactions() {
    this.reactions = [
      reaction(
        () => ({
          isReady: this.isViewerReady,
          viewer: this.viewer,
        }),
        ({ isReady, viewer }) => {
          if (isReady && viewer && isArray(this.topicStore.topic?.marksMetas)) {
            viewer?.putRemarks?.(this.topicStore.topic!.marksMetas);
          }
        },
      ),
      reaction(
        () => this.viewerSettings.toJS,
        (settings) => {
          this.viewer?.apply(settings);
          // console.log("viewer settings", settings);
        },
      ),
      reaction(
        () => this.modelToRotate,
        (type) => {
          if (type === RotateModel.None) {
            this.viewer?.resetMovableModel?.();
          }

          if (type === RotateModel.Source) {
            this.viewer?.setSourceMovable?.();
          }

          if (type === RotateModel.Target) {
            this.viewer?.setTargetMovable?.();
          }
        },
      ),
      // reaction(
      //   () => this.shouldShowMaxDistancePoints,
      //   (state) => {
      //     if (state) {
      //       this.viewer?.showMaxDistance?.();
      //     } else {
      //       this.viewer?.removeMaxDistance?.();
      //     }
      //   },
      // ),
      reaction(
        () => this.shouldShowRemarks,
        (state) => this.viewer?.setRemarksVisible?.(state),
      ),
      reaction(
        () => this.useNonSymmetricMetric,
        (state) => this.viewer?.setNonSymmMetricRecognition?.(state),
      ),
      reaction(
        () => this.isHistogramCalcInProgress,
        (state) => {
          // when start histogram calculation - remove max distance points,
          // because during calculation models will jump and points will became not actual for that moment
          if (state) {
            this.viewer?.removeMaxDistance?.();
          }

          // when histogram finish calculation and user toggle points to be shown
          // if (!state && this.shouldShowMaxDistancePoints) {
          //   this.viewer?.showMaxDistance?.();
          // }
        },
      ),
      reaction(
        () => this.remarksType,
        (remarksType) => this.viewer?.apply({ remarksType }),
      ),
      reaction(
        () => this.topicStore.topic?.onlineUsers ?? [],
        (onlineUsers) => {
          if (this.viewer && isArray(this.viewer.pointersData)) {
            this.viewer.setPointers(
              this.viewer.pointersData.filter(({ urn }) => onlineUsers.some((user) => user.urn === urn)),
            );
          }
        },
      ),
    ];
  }

  private onReceiveMarksFromViewer = debounce(async (allMarks: FlatRemark[]) => {
    try {
      const { toRemove, toAdd, toUpdate } = groupRemarks(this.topicStore.topic?.marks ?? [], allMarks);

      let topic: TopicModel | undefined;

      if (toRemove.length > 0) {
        topic = await this.topicStore.topicAPI.deleteTopicMarks(this.topicStore.topic!.id, toRemove);
      }

      if (toAdd.length > 0) {
        topic = await this.topicStore.topicAPI.addTopicMarks(this.topicStore.topic!.id, toAdd);
      }

      // TODO update remarks not working on backend
      if (toUpdate.length > 0) {
        topic = await this.topicStore.topicAPI.updateTopicMarks(this.topicStore.topic!.id, toUpdate);
      }

      if (topic) {
        this.topicStore.topic?.merge(topic);

        this.topicStore.topicWSStore.publishSystem('topicUpdate');
      }
    } catch (e) {
      NotifyService.error(e);
    }
  }, 500);

  private publishViewerChanges(data: unknown) {
    if (this.topicStore.me?.isMaster && this.topicStore.conferenceState === ConferenceState.Follow) {
      // publish latest state to all conference subscribers
      this.topicStore.topicWSStore?.publishState?.(data);
    }
  }

  private async savePreview() {
    try {
      if (this.topicStore.topic?.preview) {
        return;
      }

      const file = await this.viewer?.getScreenshot?.();

      if (file) {
        const { url } = await this.topicStore.s3UploadAPI.uploadImage(file);

        this.topicStore.topic?.setMetaValue('preview', url);

        this.topicStore.updateTopic();
      }
    } catch (e) {
      logger.error(e);
    }
  }

  private async savePreviewForce() {
    try {
      const file = await this.viewer?.getScreenshot?.();

      if (file) {
        const { url } = await this.topicStore.s3UploadAPI.uploadImage(file);

        this.topicStore.topic?.setMetaValue('preview', url);

        this.topicStore.updateTopic();
      }
    } catch (e) {
      logger.error(e);
    }
  }
}
